import React, {useEffect} from 'react';
import DocumentTitle from 'react-document-title';
import {useParams} from 'react-router-dom';

import {usePagination} from 'src/hooks/usePagination';
import {getUser} from 'src/modules/admin/services';
import {getAttributeInfo} from 'src/modules/attribute/services';
import {sortAttributes} from 'src/modules/attribute/utils/sortAttributes';
import {getAllAttributesByName} from 'src/openapi/apis/AttributeControllerApi';
import {getCurrentUser} from 'src/openapi/apis/UserControllerApi';
import {AttributeDetailInfo} from 'src/openapi/models/AttributeDetailInfo';
import {useAppDispatch, useAppSelector} from 'src/store';

import {Loading} from '@pb/kit-component-loading';

import {AttributeView} from './attribute.view';

export const AttributeController = () => {
    const dispatch = useAppDispatch();
    const params = useParams<{attributeName: string, eventAttributeId: string}>();
    const attributeName = params?.attributeName || '';
    const eventAttributeId = parseInt(params?.eventAttributeId || '');
    const attributes = useAppSelector(getAttributeInfo)[attributeName];
    const user = useAppSelector(getUser);

    const sortedAttributes = sortAttributes(attributes ?? []);
    const currentAttribute = sortedAttributes?.find(attribute => attribute.id === eventAttributeId);

    const attributesWithLiftedUpSelected = currentAttribute ? [
        currentAttribute,
        ...sortedAttributes.filter(attribute => attribute.id !== eventAttributeId),
    ] : sortedAttributes;
    const {
        slicedRowsByPage,
        hasMore,
        intersectRef,
        isVisible,
    } = usePagination<AttributeDetailInfo>(attributesWithLiftedUpSelected);

    useEffect(() => {
        if (!user) {
            dispatch(getCurrentUser());
        }
    }, [dispatch, user]);

    useEffect(() => {
        dispatch(getAllAttributesByName({name: attributeName}));
    }, [attributeName, dispatch]);

    if (!attributes?.length) {
        return (
            <Loading fast={true}/>
        );
    }

    return (
        <>
            <DocumentTitle title={`${attributeName} - Attribute`}/>
            <AttributeView {...{
                attributes: slicedRowsByPage,
                allAttributes: attributesWithLiftedUpSelected,
                hasMore,
                intersectRef,
                isVisible,
                attributeName,
                isSelectedEventCode: !!eventAttributeId && !!currentAttribute,
                user,
            }}
            />
        </>
    );
};
